import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import UserModulesProvider from './UserModulesContext';
import { useAuth0Backend } from './useAuth0Backend';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const STORAGE_KEY = 'forloop_auth_state';
const ADMIN_VIEW_KEY = 'forloop_admin_view';
const UI_STYLE_KEY = 'forloop_ui_style';

// Helper functions for localStorage
const loadAuthState = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Error loading auth state:', error);
  }
  return {
    isLoggedIn: false,
    projectKey: null,
    username: null,
    user: null
  };
};

const loadAdminView = () => {
  try {
    const stored = localStorage.getItem(ADMIN_VIEW_KEY);
    if (stored) {
      return stored;
    }
  } catch (error) {
    console.error('Error loading admin view:', error);
  }
  return null;
};

const saveAdminView = (projectKey) => {
  try {
    if (projectKey) {
      localStorage.setItem(ADMIN_VIEW_KEY, projectKey);
    } else {
      localStorage.removeItem(ADMIN_VIEW_KEY);
    }
  } catch (error) {
    console.error('Error saving admin view:', error);
  }
};

const saveAuthState = (state) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.error('Error saving auth state:', error);
  }
};

const loadUiStyle = () => {
  try {
    const stored = localStorage.getItem(UI_STYLE_KEY);
    if (stored === 'white-label' || stored === 'classic') {
      return stored;
    }
  } catch (error) {
    console.error('Error loading UI style:', error);
  }
  return 'white-label';
};

const saveUiStyle = (style) => {
  try {
    localStorage.setItem(UI_STYLE_KEY, style);
  } catch (error) {
    console.error('Error saving UI style:', error);
  }
};

const getDefaultProjectPath = (projectKey) => {
  if (!projectKey) {
    return '/settings';
  }
  if (projectKey === 'bat') {
    return `/${projectKey}/export-pricing-data`;
  }
  if (projectKey === 'pmi') {
    return `/${projectKey}/price-monitoring/retailers`;
  }
  if (projectKey === 'price_effects') {
    return `/${projectKey}/bat-effects`;
  }
  if (projectKey === 'jti') {
    return `/${projectKey}/jti-product/devices`;
  }
  if (projectKey === 'admin') {
    return `/${projectKey}/role-management`;
  }
  if (projectKey === 'matching') {
    return `/${projectKey}/bat-product-matching`;
  }
  return `/${projectKey}/powerbi`;
};

const getProjectKeyFromPath = (path) => {
  if (!path || path === '/' || path === '/settings') {
    return null;
  }
  if (path.startsWith('/supervisor') || path.startsWith('/data/')) {
    return null;
  }
  const parts = path.split('/').filter(Boolean);
  if (parts.length === 0) {
    return null;
  }
  const [maybeProjectKey] = parts;
  if (['settings', 'support'].includes(maybeProjectKey)) {
    return null;
  }
  return maybeProjectKey || null;
};

const clearAuthState = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing auth state:', error);
  }
};

export const AuthProvider = ({ children }) => {
  const { 
    isAuthenticated, 
    user, 
    isLoading, 
    loginWithRedirect, 
    logout 
  } = useAuth0();
  
  // Backend Auth0 hook
  const {
    user: backendUser,
    loading: backendLoading,
    isAuthenticated: backendIsAuthenticated,
    logout: backendLogout,
    refreshUser: backendRefreshUser,
    setUserData: setBackendUserData
  } = useAuth0Backend();
  
  const [projectKey, setProjectKey] = useState(null);
  const [userProjectKey, setUserProjectKey] = useState(null);
  const [username, setUsername] = useState(null);
  const [adminViewProjectKey, setAdminViewProjectKey] = useState(() => loadAdminView());
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [simpleLoginState, setSimpleLoginState] = useState(() => loadAuthState());
  const [uiStyle, setUiStyleState] = useState(() => loadUiStyle());
  const navigate = useNavigate();

  const mapUserToProjectKey = (user) => {
    return user?.project_key || null;
  };

  // Helper returns both username and project_key
  const mapUserToUsernameAndProjectKey = (user) => {
    const projectKey = mapUserToProjectKey(user);
    const fallbackUsername = user.email || user.nickname || user.name || '';
    return {
      username: projectKey || fallbackUsername,
      projectKey: projectKey
    };
  };

  useEffect(() => {
    console.log('Auth state changed:', { 
      isAuthenticated, 
      isLoading, 
      user: user?.email,
      userProjectKey: user?.project_key,
      backendIsAuthenticated,
      backendLoading,
      backendUser: backendUser?.email,
      backendUserProjectKey: backendUser?.project_key,
      currentPath: window.location.pathname
    });
    
    // Don't process navigation if still loading - wait for user data to be ready
    if (isLoading || backendLoading) {
      console.log('Still loading auth data, skipping navigation logic');
      return;
    }
    
    // Check Auth0 SPA authentication
    if (isAuthenticated && user) {
      // Check if user is admin
      const userEmail = user.email?.toLowerCase() || '';
      const userRole = user.role || null;
      const isSuperAdminUser = userEmail === 'dominik@forloop.ai';
      const isAdminUser = userRole === 'Admin' || isSuperAdminUser;
      setIsSuperAdmin(isSuperAdminUser);
      setIsAdmin(isAdminUser);
      if (!isAdminUser && adminViewProjectKey) {
        setAdminViewProjectKey(null);
        saveAdminView(null);
      }
      
      // Use project_key from backend if available
      const backendProjectKey = user.project_key || null;
      const { username: mappedUsername, projectKey: mappedProjectKey } = mapUserToUsernameAndProjectKey({
        ...user,
        project_key: backendProjectKey
      });
      
      // Use admin view project_key if set, otherwise use actual project_key
      const effectiveProjectKey = adminViewProjectKey || mappedProjectKey;
      // When in admin view, username should match the admin view project_key
      const effectiveUsername = adminViewProjectKey || mappedUsername;
      setProjectKey(effectiveProjectKey);
      setUserProjectKey(mappedProjectKey);
      setUsername(effectiveUsername);
      
      // Save Auth0 state to localStorage
      const authState = {
        isLoggedIn: true,
        projectKey: mappedProjectKey,
        username: mappedUsername,
        user: {
          name: user.name,
          email: user.email,
          nickname: user.nickname,
          picture: user.picture,
          sub: user.sub,
          role: user.role || null
        },
        authType: 'auth0-spa'
      };
      saveAuthState(authState);
      
      // Console logging for user data
      console.log('=== Auth0 SPA Login Data ===');
      console.log('User:', user);
      console.log('Email:', user.email);
      console.log('Name:', user.name);
      console.log('Nickname:', user.nickname);
      console.log('Picture:', user.picture);
      console.log('Sub:', user.sub);
      console.log('Mapped Username:', mappedUsername);
      console.log('Mapped Project Key:', mappedProjectKey);
      console.log('========================');
      
      // Only navigate if we're not already on a project-specific route or special routes
      const currentPath = window.location.pathname;
      const specialRoutes = ['/supervisor', '/data/anuga', '/settings', '/support'];
      const isSpecialRoute = specialRoutes.some(route => currentPath === route || currentPath.startsWith(route + '/'));
      
      // Extract project key from current path
      const pathProjectKey = getProjectKeyFromPath(currentPath);
      
      console.log('Auth0 SPA navigation check:', {
        currentPath,
        pathProjectKey,
        effectiveProjectKey,
        isSpecialRoute
      });
      
      if (!isSpecialRoute) {
        // Only prevent redirect if we're switching projects (URL has different project than state)
        // This prevents redirecting when switching projects and the state hasn't updated yet
        if (pathProjectKey && pathProjectKey !== effectiveProjectKey && effectiveProjectKey) {
          // We're on a different project route than our state - trust the URL
          // The state will update when the user data refreshes
          console.log('On project route:', pathProjectKey, 'but state has:', effectiveProjectKey, '- trusting URL, NOT redirecting');
          return;
        }
        
        // Normal navigation logic
        if (effectiveProjectKey && !currentPath.includes('/' + effectiveProjectKey + '/')) {
          console.log('Navigating to dashboard from:', currentPath, 'to:', getDefaultProjectPath(effectiveProjectKey));
          navigate(getDefaultProjectPath(effectiveProjectKey));
        } else if (!effectiveProjectKey && !['/settings', '/support'].includes(currentPath)) {
          if (pathProjectKey) {
            return;
          }
          navigate('/settings');
        }
      }
    } 
    // Check Backend Auth0 authentication
    else if (backendIsAuthenticated && backendUser) {
      // Check if user is admin
      const userEmail = backendUser.email?.toLowerCase() || '';
      const userRole = backendUser.role || null;
      const isSuperAdminUser = userEmail === 'dominik@forloop.ai';
      const isAdminUser = userRole === 'Admin' || isSuperAdminUser;
      setIsSuperAdmin(isSuperAdminUser);
      setIsAdmin(isAdminUser);
      if (!isAdminUser && adminViewProjectKey) {
        setAdminViewProjectKey(null);
        saveAdminView(null);
      }
      
      // Use project_key from backend if available
      const backendProjectKey = backendUser.project_key || null;
      const { username: mappedUsername, projectKey: mappedProjectKey } = mapUserToUsernameAndProjectKey({
        ...backendUser,
        project_key: backendProjectKey
      });
      
      // Use admin view project_key if set, otherwise use actual project_key
      const effectiveProjectKey = adminViewProjectKey || mappedProjectKey;
      // When in admin view, username should match the admin view project_key
      const effectiveUsername = adminViewProjectKey || mappedUsername;
      setProjectKey(effectiveProjectKey);
      setUserProjectKey(mappedProjectKey);
      setUsername(effectiveUsername);
      
      // Save Backend Auth0 state to localStorage
      const authState = {
        isLoggedIn: true,
        projectKey: mappedProjectKey,
        username: mappedUsername,
        user: {
          name: backendUser.name,
          email: backendUser.email,
          nickname: backendUser.nickname || backendUser.name,
          picture: backendUser.picture,
          sub: backendUser.sub,
          role: backendUser.role || null
        },
        authType: 'auth0-backend'
      };
      saveAuthState(authState);
      
      // Console logging for user data
      console.log('=== Auth0 Backend Login Data ===');
      console.log('User:', backendUser);
      console.log('Email:', backendUser.email);
      console.log('Name:', backendUser.name);
      console.log('Nickname:', backendUser.nickname);
      console.log('Picture:', backendUser.picture);
      console.log('Sub:', backendUser.sub);
      console.log('Mapped Username:', mappedUsername);
      console.log('Mapped Project Key:', mappedProjectKey);
      console.log('========================');
      
      // Only navigate if we're not already on a project-specific route or special routes
      const currentPath = window.location.pathname;
      const specialRoutes = ['/supervisor', '/data/anuga', '/settings', '/support'];
      const isSpecialRoute = specialRoutes.some(route => currentPath === route || currentPath.startsWith(route + '/'));
      
      // Extract project key from current path
      const pathProjectKey = getProjectKeyFromPath(currentPath);
      
      console.log('Backend Auth0 navigation check:', {
        currentPath,
        pathProjectKey,
        effectiveProjectKey,
        isSpecialRoute
      });
      
      if (!isSpecialRoute) {
        // Only prevent redirect if we're switching projects (URL has different project than state)
        // This prevents redirecting when switching projects and the state hasn't updated yet
        if (pathProjectKey && pathProjectKey !== effectiveProjectKey && effectiveProjectKey) {
          // We're on a different project route than our state - trust the URL
          // The state will update when the user data refreshes
          console.log('On project route:', pathProjectKey, 'but state has:', effectiveProjectKey, '- trusting URL, NOT redirecting');
          return;
        }
        
        // Normal navigation logic
        if (effectiveProjectKey && !currentPath.includes('/' + effectiveProjectKey + '/')) {
          console.log('Navigating to dashboard from:', currentPath, 'to:', getDefaultProjectPath(effectiveProjectKey));
          navigate(getDefaultProjectPath(effectiveProjectKey));
        } else if (!effectiveProjectKey && !['/settings', '/support'].includes(currentPath)) {
          if (pathProjectKey) {
            return;
          }
          navigate('/settings');
        }
      }
    }
    else if (!isAuthenticated && !isLoading && !backendIsAuthenticated && !backendLoading) {
      // No live auth, but localStorage still has an auth0-backend session record.
      // The silent renewal in useAuth0Backend will redirect to Auth0 if needed.
      // In the meantime, use the saved project key to navigate the user to the right
      // place so they don't see a blank / settings page while the redirect is pending.
      if (simpleLoginState.isLoggedIn && simpleLoginState.authType === 'auth0-backend') {
        // Admin view project key takes precedence over the user's real project key
        const savedProjectKey = adminViewProjectKey || simpleLoginState.projectKey;
        const currentPath = window.location.pathname;
        const specialRoutes = ['/supervisor', '/data/anuga', '/settings', '/support'];
        const isSpecialRoute = specialRoutes.some(route => currentPath === route || currentPath.startsWith(route + '/'));
        if (!isSpecialRoute && savedProjectKey) {
          const pathProjectKey = getProjectKeyFromPath(currentPath);
          if (!pathProjectKey && !currentPath.includes('/' + savedProjectKey + '/')) {
            navigate(getDefaultProjectPath(savedProjectKey));
          }
        }
      } else {
        // Only clear Auth0 state, keep simple login state
        setProjectKey(null);
        setUsername(null);
      }
    }
  }, [isAuthenticated, user, isLoading, backendIsAuthenticated, backendUser, backendLoading, simpleLoginState, adminViewProjectKey, navigate]);

  const handleLogout = () => {
    // Clear localStorage
    clearAuthState();
    
    // Clear simple login state
    setSimpleLoginState({
      isLoggedIn: false,
      projectKey: null,
      username: null,
      user: null
    });
    
    // If Auth0 SPA is authenticated, use Auth0 SPA logout
    if (isAuthenticated) {
      logout({ logoutParams: { returnTo: window.location.origin } });
    } 
    // If Backend Auth0 is authenticated, use backend logout
    else if (backendIsAuthenticated) {
      backendLogout();
    } 
    else {
      // For simple login, navigate to home
      navigate('/');
    }
    
    // Clear Auth0 state
    setProjectKey(null);
    setUsername(null);
  };

  const handleSimpleLogin = (loginData) => {
    const authState = {
      isLoggedIn: true,
      projectKey: loginData.projectKey,
      username: loginData.username,
      user: {
        name: loginData.username,
        email: loginData.username,
        nickname: loginData.username
      },
      authType: 'simple'
    };
    
    setSimpleLoginState(authState);
    saveAuthState(authState);
  };

  // Determine the effective authentication state
  const effectiveIsAuthenticated = isAuthenticated || backendIsAuthenticated || simpleLoginState.isLoggedIn;
  const effectiveUser = user || backendUser || (simpleLoginState.isLoggedIn && simpleLoginState.user ? 
    simpleLoginState.user : null);
  // DB project key (live, from backend) takes precedence; fall back to localStorage
  const effectiveProjectKey = projectKey || simpleLoginState.projectKey;
  const effectiveUsername = username || simpleLoginState.username;
  const effectiveIsLoading = isLoading || backendLoading;
  // Preserve auth0-backend type from localStorage so that components (e.g. project
  // access request) keep working even while the backend check is still in-flight or
  // after a session was just silently renewed.
  const effectiveAuthType = isAuthenticated ? 'auth0-spa'
    : (backendIsAuthenticated ? 'auth0-backend'
    : (simpleLoginState.isLoggedIn && simpleLoginState.authType === 'auth0-backend'
        ? 'auth0-backend'
        : (simpleLoginState.isLoggedIn ? 'simple' : null)));

  const setAdminView = (projectKey) => {
    setAdminViewProjectKey(projectKey);
    saveAdminView(projectKey);
    // Reload modules with new project_key
    if (window.location.reload) {
      // Small delay to ensure state is updated
      setTimeout(() => {
        window.location.reload();
      }, 100);
    }
  };

  const setUiStyle = (nextStyle) => {
    const allowWhiteLabel = ['jti', 'jti-global', 'jti-sweden', 'bat', 'pmi'].includes(effectiveProjectKey);
    const normalized = allowWhiteLabel && nextStyle === 'white-label' ? 'white-label' : 'classic';
    setUiStyleState(normalized);
    saveUiStyle(normalized);
  };

  const clearAdminView = () => {
    setAdminViewProjectKey(null);
    saveAdminView(null);
    // Reload modules with original project_key
    if (window.location.reload) {
      setTimeout(() => {
        window.location.reload();
      }, 100);
    }
  };

  const allowWhiteLabel = ['jti', 'jti-global', 'jti-sweden', 'bat', 'pmi'].includes(effectiveProjectKey);
  const effectiveUiStyle = allowWhiteLabel ? uiStyle : 'classic';

  const value = {
    isAuthenticated: effectiveIsAuthenticated,
    user: effectiveUser,
    isLoading: effectiveIsLoading,
    projectKey: effectiveProjectKey,
    username: effectiveUsername,
    authType: effectiveAuthType,
    isAdmin,
    isSuperAdmin,
    userProjectKey,
    adminViewProjectKey,
    setAdminView,
    clearAdminView,
    uiStyle: effectiveUiStyle,
    setUiStyle,
    loginWithRedirect,
    logout: handleLogout,
    handleSimpleLogin,
    refreshUser: backendRefreshUser,
    setBackendUserData
  };

  return (
    <UserModulesProvider>
      <AuthContext.Provider value={value}>
        {children}
      </AuthContext.Provider>
    </UserModulesProvider>
  );
};
